// Affiliate referral capture, backed by the Rekomi loader script.
//
// The script (added in __root.tsx behind VITE_REKOMI_PROGRAM_ID) reads the
// `?via=<slug>` param from affiliate links and stores the referral in a
// first-party cookie. At checkout we read it back and send it as `rekomiRef`,
// which the backend stamps into Polar metadata as `rekomi_ref` so Rekomi can
// credit the affiliate off the paid order.

declare global {
  interface Window {
    Rekomi?: {
      getReferral?: () => string | null | undefined;
    };
  }
}

/**
 * Polar caps metadata values at 500 chars and the backend schema rejects
 * anything longer. A referral that would fail validation must be dropped here
 * instead: a broken ref should cost the affiliate a commission, never cost the
 * customer their checkout.
 */
const MAX_REFERRAL_LENGTH = 500;

/**
 * The affiliate referral captured by the Rekomi script, or undefined.
 *
 * Undefined is the common case, not the error case: the script is absent when
 * the env var is unset, blocked by ad blockers, or not loaded yet, and most
 * visitors simply did not arrive through an affiliate link. Checkout must never
 * depend on this value, so any failure mode collapses to undefined.
 */
export const getRekomiReferral = (): string | undefined => {
  if (typeof window === "undefined") return undefined;

  try {
    const referral = window.Rekomi?.getReferral?.();
    if (typeof referral !== "string") return undefined;

    const trimmed = referral.trim();
    if (trimmed.length === 0 || trimmed.length > MAX_REFERRAL_LENGTH) return undefined;

    return trimmed;
  } catch {
    // A misbehaving third-party script must never break the buy button.
    return undefined;
  }
};
